<template>
  <EditInformationModal
    :open="existing_record_modal_config.open"
    @close-modal="existing_record_modal_config.open = false"
    :modal_config="existing_record_modal_config"
    @confirm="handleDataRefresh"
  />
  <NewJointOrEntityModal
    :open="new_joint_or_entity_modal_config.open"
    @close-modal="new_joint_or_entity_modal_config.open = false"
    @created="handleNewRecordCreated"
    :modal_config="new_joint_or_entity_modal_config"
  />

  <EditTrustedContactModal
    :open="edit_trusted_contact_modal_config.open"
    @close-modal="edit_trusted_contact_modal_config.open = false"
    :modal_config="edit_trusted_contact_modal_config"
    @confirm="handleDataRefresh"
  />
  <QuestionnaireModal
    :open="questionnaire_modal_config.open"
    @close-modal="questionnaire_modal_config.open = false"
    :modal_config="questionnaire_modal_config"
    @confirm="handleDataRefresh"
  />

  <TrustedContactOptOutModal
    :open="trusted_contact_opt_out_modal_config_open"
    @close-modal="trusted_contact_opt_out_modal_config_open = false"
    @confirm="handleDataRefresh"
  />

  <DeleteJointOrEntityModal
    :open="delete_confirmation_modal_config.open"
    @close-modal="delete_confirmation_modal_config.open = false"
    :modal_config="delete_confirmation_modal_config"
    @deleted="handleRecordDeleted"
  />

  <NewBeneficialOwnerModal
    :open="new_ben_owner_modal_config.open"
    @close-modal="new_ben_owner_modal_config.open = false"
    :modal_config="new_ben_owner_modal_config"
    @confirm="handleDataRefresh"
  />

  <EditBeneficialOwnerModal
    :open="edit_ben_owner_modal_config.open"
    @close-modal="edit_ben_owner_modal_config.open = false"
    :modal_config="edit_ben_owner_modal_config"
    @confirm="handleDataRefresh"
  />

  <ConfirmDeleteBenOwnerModal
    :open="delete_ben_owner_modal_config.open"
    @close-modal="delete_ben_owner_modal_config.open = false"
    :modal_config="delete_ben_owner_modal_config"
    @confirm="handleDataRefresh"
  />

  <AddBenOwnerToEntityModal
    :open="add_ben_owner_to_entity_modal_config.open"
    @close-modal="add_ben_owner_to_entity_modal_config.open = false"
    :modal_config="add_ben_owner_to_entity_modal_config"
    @confirm="handleDataRefresh"
  />

  <ApplyBankAccountModal
    :open="apply_bank_modal.open"
    :bank_account_id="apply_bank_modal.bank_account_id"
    @close-modal="apply_bank_modal.open = false"
    @applied="handleBankAccountApplied"
  />
  <div class="lg:px-6 xl:px-0">
    <BreadCrumbs
      class="mb-[1.625rem]"
      :crumbs="[
        {
          name: 'Profile',
          path: '/profile',
        },
      ]"
    />
    <div class="mb-[2.125rem] hidden lg:block">
      <div
        class="mb-[.375rem] text-grey-8 dark:text-grey-2 dark:text-opacity-40 text-opacity-40 font-league font-semibold text-base"
      >
        Profile
      </div>
      <div class="text-strongest font-league text-[2rem] font-semibold tracking-[-0.64px]">
        Your Profile
      </div>
    </div>

    <div class="mb-8 lg:mb-40">
      <TabView
        :tabs="tabs"
        vertical
        :activeTab="route.query?.selectedTab === 'banking' ? 'banking' : activeProfileTab"
        @update:activeTab="
          (key) => {
            activeProfileTab = key;
            profileSectionViewed(key);
          }
        "
        @tab-action="handleTabAction"
      >
        <template
          v-for="subtab in personalSubtabs"
          #[`${subtab.slot_name}`]
          :key="subtab.slot_name"
        >
          <PaperCard
            v-if="owner_detail_groups.includes(subtab.label)"
            :is_collapsable="false"
            class="mb-5"
          >
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">
                {{ subtab.label }}
              </div>
            </template>

            <template #ctas>
              <Button
                variant="paper-primary"
                @click="editInformation('owner_contact', subtab.label)"
              >
                <template #label>
                  <div class="hidden md:flex">Edit Information</div>
                  <div class="md:hidden flex">Edit</div>
                </template>
              </Button>
            </template>

            <template #body>
              <div
                v-if="subtab.label == 'ID Information' && daysUntilExpire <= 30"
                class="mb-[1.625rem]"
              >
                <AsteriskNotice
                  variant="error"
                  size="md"
                  class="mb-1"
                  text="ID Info Expiring"
                  contained
                >
                  <div class="font-inter text-strongest font-medium" v-if="daysUntilExpire > 0">
                    Your ID is expiring in {{ daysUntilExpire }} days. Please provide updated
                    information.
                  </div>
                  <div class="font-inter text-strongest font-medium" v-else>
                    Your ID has expired. Please provide updated information.
                  </div>
                </AsteriskNotice>
              </div>

              <Form
                :record_id="dataStore.contact.record_id"
                :schema_array="getSchema(`#personal.${slugify(subtab.label)}`)"
                :state_object="getObject('owner_contact', null, subtab.label)"
                :is_disabled="true"
                variant="primary"
              />
            </template>
          </PaperCard>
        </template>

        <template v-for="subtab in jointContactSubtabs" #[`${subtab.slot_name}`]>
          <div class="flex justify-between mb-6 items-center">
            <div
              class="text-[1.75rem] font-semibold font-league tracking-[-0.56px] text-strongest flex items-center"
            >
              {{ subtab.label }}
              <span class="rounded-full bg-container-bg ml-4 py-2 px-4 text-sm"
                >{{ active_investments(subtab.record_id, "joint_contact")?.length }} signed
                investment{{
                  active_investments(subtab.record_id, "joint_contact")?.length !== 1 ? "s" : ""
                }}</span
              >
            </div>
            <Button
              variant="primary-grey"
              class="hidden md:flex"
              @click="deleteJointOrEntity('joint_contact', subtab.record_id)"
              v-if="active_investments(subtab.record_id, 'joint_contact')?.length === 0"
            >
              <template #icon>
                <Icon name="Trash2" class="w-4 h-4 stroke-error" />
              </template>
              <template #label>Delete joint contact</template>
            </Button>
          </div>
          <PaperCard
            :is_collapsable="false"
            v-for="group of joint_contact_detail_groups"
            class="mb-5"
          >
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">{{ group }}</div>
            </template>

            <template #ctas>
              <Button
                variant="paper-primary"
                @click="editInformation('joint_contact', group, subtab.record_id)"
              >
                <template #label>
                  <div class="hidden md:flex">Edit Information</div>
                  <div class="md:hidden flex">Edit</div></template
                >
              </Button>
            </template>

            <template #body>
              <div
                v-if="group == 'ID Information' && daysUntilExpireJoint(subtab.record_id) <= 30"
                class="mb-[1.625rem]"
              >
                <AsteriskNotice
                  variant="error"
                  size="md"
                  class="mb-1"
                  text="ID Info Expiring"
                  contained
                >
                  <div
                    class="font-inter text-strongest font-medium"
                    v-if="daysUntilExpireJoint(subtab.record_id) > 0"
                  >
                    This joint investor's ID is expiring in
                    {{ daysUntilExpireJoint(subtab.record_id) }} days. Please provide updated
                    information.
                  </div>
                  <div class="font-inter text-strongest font-medium" v-else>
                    This joint investor's ID has expired. Please provide updated information.
                  </div>
                </AsteriskNotice>
              </div>

              <Form
                :record_id="subtab.record_id"
                :schema_array="getSchema(`#joint-contacts.${slugify(group)}`)"
                :state_object="getObject('joint_contact', subtab.record_id, group)"
                :is_disabled="true"
                variant="primary"
              />
            </template>
          </PaperCard>
        </template>
        <template v-for="subtab in entitySubtabs" #[`${subtab.slot_name}`]>
          <div class="flex justify-between mb-6 items-center">
            <div class="text-[1.75rem] font-semibold font-league tracking-[-0.56px] text-strongest">
              {{ subtab.label }}
              <span class="rounded-full bg-container-bg ml-4 py-2 px-4 text-sm"
                >{{ active_investments(subtab.record_id, "entity_account").length }} signed
                investments</span
              >
            </div>
            <Button
              variant="primary-grey"
              class="hidden md:flex"
              @click="deleteJointOrEntity('entity_account', subtab.record_id)"
              v-if="active_investments(subtab.record_id, 'entity_account').length === 0"
            >
              <template #label>Delete entity</template>
            </Button>
          </div>
          <PaperCard :is_collapsable="false" v-for="group of account_detail_groups" class="mb-5">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">{{ group }}</div>
            </template>

            <template #ctas>
              <Button
                variant="paper-primary"
                @click="editInformation('entity_account', group, subtab.record_id)"
              >
                <template #label>
                  <div class="hidden md:flex">Edit Information</div>
                  <div class="md:hidden flex">Edit</div></template
                >
              </Button>
            </template>

            <template #body>
              <Form
                :record_id="subtab.record_id"
                :schema_array="getSchema(`#entities.${slugify(group)}`)"
                :state_object="getObject('entity_account', subtab.record_id)"
                :is_disabled="true"
                variant="primary"
              />
            </template>
          </PaperCard>
          <PaperCard :is_collapsable="false" class="mb-5">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">Beneficial Owners</div>
              <AsteriskNotice
                v-if="active_investments(subtab.record_id, 'entity_account').length"
                variant="info"
                size="medium"
                text="Beneficial owners can not be added once an entity has one or more active investments."
              />
            </template>

            <template #ctas>
              <Button
                variant="paper-primary"
                class="items-center flex"
                v-if="active_investments(subtab.record_id, 'entity_account').length == 0"
                @click="addBenOwnerToEntity(subtab.record_id)"
              >
                <template #label> Add Beneficial Owner</template>
              </Button>
            </template>

            <template #body>
              <Table
                :table_headers="[
                  { label: 'Name', field_name: 'name', field_type: 'string' },
                  { label: 'Street', field_name: 'street1', field_type: 'string' },
                  { label: 'City', field_name: 'city', field_type: 'string' },
                  { label: 'State', field_name: 'state', field_type: 'string' },
                ]"
                :table_data="entity_beneficialOwners(subtab.record_id)"
                variant="primary"
                :has_actions="true"
                :fields_with_custom_styles="[]"
                mobile_primary_field="name"
                :hide_headers="false"
                :hide_scrollbar="false"
                empty_icon="Users"
                empty_primary_text="No beneficial owners found"
                empty_secondary_text="Add a beneficial owner to get started"
                :column_gap_px="36"
              >
                <template v-slot:actions="entry">
                  <div class="gap-1 flex">
                    <Button
                      variant="paper-primary"
                      class="items-center flex"
                      v-if="entity_beneficialOwners(subtab.record_id).length > 1"
                      @click="deleteBeneficialOwner(entry.row, false, subtab.record_id)"
                    >
                      <template #icon>
                        <Icon name="Trash2" class="w-5 h-5 stroke-error" />
                      </template>
                    </Button>
                    <Button
                      variant="paper-primary"
                      class="items-center flex"
                      v-if="
                        !entry.row.is_primary_contact &&
                        entity_beneficialOwners(subtab.record_id).length > 1
                      "
                      @click="editBeneficialOwner(entry.row)"
                    >
                      <template #icon>
                        <Icon name="Pencil" class="w-5 h-5" />
                      </template>
                    </Button>
                  </div>
                </template>
              </Table>
            </template>
          </PaperCard>
        </template>
        <template #entities.beneficial-owners>
          <div class="flex justify-between mb-6 items-center">
            <div class="text-[1.75rem] font-semibold font-league tracking-[-0.56px] text-strongest">
              Beneficial Owners
            </div>
            <Button
              variant="paper-primary"
              class="items-center hidden md:flex"
              @click="addBeneficialOwner()"
            >
              <template #label> Add Beneficial Owner</template>
              <template #icon>
                <Icon name="Plus" class="w-5 h-5" />
              </template>
            </Button>
          </div>
          <div>
            <Table
              :table_headers="[
                { label: 'Name', field_name: 'name', field_type: 'string' },
                { label: 'Street', field_name: 'street1', field_type: 'string' },
                { label: 'City', field_name: 'city', field_type: 'string' },
                { label: 'State', field_name: 'state', field_type: 'string' },
              ]"
              :table_data="beneficialOwners"
              variant="secondary"
              :has_actions="true"
              :fields_with_custom_styles="[]"
              mobile_primary_field="name"
              :hide_headers="false"
              :hide_scrollbar="false"
              empty_icon="Users"
              empty_primary_text="No beneficial owners found"
              empty_secondary_text="Add a beneficial owner to get started"
              :column_gap_px="36"
            >
              <template v-slot:actions="entry">
                <div class="gap-1 flex">
                  <Button
                    variant="paper-primary"
                    class="items-center flex"
                    @click="editBeneficialOwner(entry.row)"
                  >
                    <template #icon>
                      <Icon name="Pencil" class="w-5 h-5" />
                    </template>
                  </Button>
                  <Button
                    variant="paper-primary"
                    class="items-center flex"
                    v-if="allowBenOwnerDelete(entry.row)"
                    @click="deleteBeneficialOwner(entry.row, true, null)"
                  >
                    <template #icon>
                      <Icon name="Trash2" class="w-5 h-5 stroke-error" />
                    </template>
                  </Button>
                </div>
              </template>
            </Table>
          </div>
        </template>

        <template #banking>
          <div
            v-if="newBankAccountId"
            class="border-2 border-info/30 bg-info/5 rounded-xl p-4 lg:p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5"
          >
            <div class="flex items-start gap-3">
              <div class="bg-info/10 rounded-lg p-2 shrink-0">
                <Icon name="AlertCircle" class="w-5 h-5 stroke-info" />
              </div>
              <div>
                <div class="font-inter font-semibold text-strongest text-base">
                  New bank account added
                </div>
                <div class="font-inter text-sm text-strong mt-0.5">
                  Apply it as a distribution account to your investments.
                </div>
              </div>
            </div>
            <div class="flex gap-2 shrink-0 ml-10 lg:ml-0">
              <Button variant="paper-primary" size="sm" @click="dismissBankBanner">
                <template #label>Dismiss</template>
              </Button>
              <Button
                variant="primary-grey"
                size="sm"
                @click="apply_bank_modal = { open: true, bank_account_id: newBankAccountId }"
              >
                <template #label>Apply to Investments</template>
              </Button>
            </div>
          </div>
          <PaperCard :is_collapsable="false">
            <template #header>
              <div class="mr-[1.625rem]">
                <div class="text-xl font-semibold text-strongest font-league mb-3">Banking</div>
                <div class="text-sm text-strong font-inter">
                  Below are all financial accounts available to use for funding or distribution. To
                  add an additional bank account, click on the Connect Bank Account button.
                </div>
              </div>
            </template>

            <template #ctas>
              <Button
                class="hidden md:flex"
                variant="primary-grey"
                @click="handleConnectBankAccount()"
              >
                <template #label>Connect Bank Account</template>
              </Button>
            </template>

            <template #body>
              <div class="flex flex-col gap-4" v-if="bankAccounts.length">
                <BankCard
                  v-for="bank in bankAccounts"
                  :key="bank.record_id"
                  :bank="{
                    ...bank,
                    account_number: bank.account_number,
                    routing_number: bank.routing_number,
                  }"
                  :transparent-background="false"
                />
              </div>
              <div class="flex flex-col gap-4 my-6" v-else>
                <div class="text-strong font-inter text-center font-semibold">
                  No bank accounts connected yet. Use 'Connect bank account' to get started.
                </div>
              </div>
            </template>
          </PaperCard>
        </template>
        <template #trusted-contacts>
          <PaperCard :is_collapsable="false" class="mb-5" :has_header="false">
            <template #body>
              <div class="text-strongest font-inter font-semibold text-base mb-2">
                A trusted contact person(s) is an individual (age 18 or older) identified by you,
                whom Phoenix Energy could contact and disclose information about your account:
              </div>
              <ul class="list-disc pl-5 font-inter text-strong text-sm">
                <li>to address possible financial exploitation;</li>
                <li>
                  to confirm specifies of your current contact information, health status (including
                  capacity and well being), or the identity of any legal guardian, executor,
                  trustee, or holder of a power of attorney; or
                </li>
                <li>
                  as otherwise permitted by Financial Industry Regulatory Authority Rule 2165
                  (Financial Explotation of Specified Adults).
                </li>
              </ul>
              <BaseInput
                label="I decline to designate a Trusted Contact Person at this time."
                field_name="opted_out_trusted_contact_form"
                field_type="boolean"
                v-model="opted_out_trusted_contact_form"
                variant="primary"
              />
            </template>
          </PaperCard>
          <PaperCard :is_collapsable="false" class="mb-5" v-if="!opted_out_trusted_contact_form">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">Primary</div>
            </template>

            <template #ctas>
              <Button variant="paper-primary" @click="editTrustedContactInformation('Primary')">
                <template #label>
                  <div class="hidden md:flex">Edit Information</div>
                  <div class="md:hidden flex">Edit</div>
                </template>
              </Button>
            </template>

            <template #body>
              <Form
                :schema_array="getSchema(`trusted_contact`)"
                :state_object="getObject('trusted_contact', 'Primary')"
                :is_disabled="true"
                variant="primary"
              />
            </template>
          </PaperCard>
          <PaperCard :is_collapsable="false" class="mb-5" v-if="!opted_out_trusted_contact_form">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">Alternate</div>
            </template>

            <template #ctas>
              <Button variant="paper-primary" @click="editTrustedContactInformation('Alternate')">
                <template #label>
                  <div class="hidden md:flex">Edit Information</div>
                  <div class="md:hidden flex">Edit</div>
                </template>
              </Button>
            </template>

            <template #body>
              <Form
                :schema_array="getSchema(`trusted_contact`)"
                :state_object="getObject('trusted_contact', 'Alternate')"
                :is_disabled="true"
                variant="primary"
              />
            </template>
          </PaperCard>
        </template>
        <template #investor-questionnaire>
          <PaperCard :is_collapsable="false" class="mb-5">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">Income Information</div>
            </template>

            <template #ctas>
              <Button variant="paper-primary" @click="editQuestionnaire('Income Information')">
                <template #label>
                  <div class="hidden md:flex">Edit Information</div>
                  <div class="md:hidden flex">Edit</div></template
                >
              </Button>
            </template>
            <template #body>
              <Form
                :schema_array="getSchema(`investor_questionnaire_income`)"
                :state_object="getObject('investor_questionnaire', 'income')"
                :is_disabled="true"
                variant="primary"
              />
            </template>
          </PaperCard>
          <PaperCard :is_collapsable="false" class="mb-5">
            <template #header>
              <div class="text-xl font-semibold text-strongest font-league">Questionnaire</div>
            </template>

            <template #ctas>
              <Button variant="paper-primary" @click="editQuestionnaire('Questionnaire')">
                <template #label>
                  <div class="hidden md:flex">Edit Information</div>
                  <div class="md:hidden flex">Edit</div></template
                >
              </Button>
            </template>
            <template #body>
              <QuestionnaireDisplay
                :schema_array="getSchema(`investor_questionnaire`)"
                :state_object="getObject('investor_questionnaire', 'questionnaire')"
              />
            </template>
          </PaperCard>
        </template>
      </TabView>
    </div>
  </div>

  <div
    class="fixed inset-x-0 bottom-0 rounded-t-[20px] z-20 bg-purchase-flow-bottom-nav-gradient btn-bg-swap md:hidden p-4 flex items-center justify-center gap-2"
    v-if="
      showBottomDeleteButton ||
      showBottomAddButton ||
      activeProfileTab === 'entities.beneficial-owners' ||
      activeProfileTab === 'banking'
    "
  >
    <Button
      v-if="showBottomDeleteButton"
      variant="primary-grey"
      class="w-full"
      @click="
        deleteJointOrEntity(
          activeProfileTab.split('.')[0] === 'joint-contacts' ? 'joint_contact' : 'entity_account',
          activeProfileTab.split('.')[1]
        )
      "
    >
      <template #icon>
        <Icon name="Trash2" class="w-4 h-4 stroke-error" />
      </template>

      <template #label
        >Delete
        {{
          activeProfileTab.split(".")[0] === "joint-contacts" ? "joint contact" : "entity"
        }}</template
      >
    </Button>
    <Button
      v-if="showBottomAddButton"
      variant="primary-grey"
      class="w-full"
      @click="
        addNewJointOrEntity(
          activeProfileTab.split('.')[0] === 'joint-contacts' ? 'joint_contact' : 'entity_account'
        )
      "
    >
      <template #icon>
        <Icon name="Plus" class="w-4 h-4" />
      </template>

      <template #label
        >Add
        {{
          activeProfileTab.split(".")[0] === "joint-contacts" ? "joint contact" : "entity"
        }}</template
      >
    </Button>
    <Button
      v-if="activeProfileTab === 'entities.beneficial-owners'"
      variant="primary-grey"
      class="w-full"
      @click="addBeneficialOwner()"
    >
      <template #icon>
        <Icon name="Plus" class="w-4 h-4" />
      </template>

      <template #label>Add Beneficial Owner</template>
    </Button>
    <Button
      v-if="activeProfileTab === 'banking'"
      variant="primary-grey"
      class="w-full"
      @click="handleConnectBankAccount()"
    >
      <template #icon>
        <Icon name="Plus" class="w-4 h-4" />
      </template>

      <template #label>Connect bank account</template>
    </Button>
  </div>
</template>
<script setup>
import { ref, watch, onMounted, computed, onUnmounted } from "vue";
import { useSchemaStore, useUIStore, useDataStore, useCrudStore } from "@/stores";

import {
  PaperCard,
  Button,
  Icon,
  BaseInput,
  Table,
  Form,
  BreadCrumbs,
  TabView,
  AsteriskNotice,
  BankCard,
  ApplyBankAccountModal,
} from "@/components";

import {
  EditInformationModal,
  NewJointOrEntityModal,
  TrustedContactOptOutModal,
  EditTrustedContactModal,
  DeleteJointOrEntityModal,
  QuestionnaireModal,
  NewBeneficialOwnerModal,
  EditBeneficialOwnerModal,
  ConfirmDeleteBenOwnerModal,
  AddBenOwnerToEntityModal,
  QuestionnaireDisplay,
} from "../InvestorProfile";
import { useRoute, useRouter } from "vue-router";

import { usePlaidLink } from "@/composables/usePlaidLink";
import { useAnalytics, syncContactPersonProperties } from "@/composables/useAnalytics";
import { get } from "lodash";

const BANNER_STORAGE_KEY = "pe_new_bank_account_id";

const {
  profileSectionViewed,
  profileEditOpened,
  profileEditSaved,
  jointContactAdded,
  entityAdded,
  connectBankAccountClicked,
  questionnaireSubmitted,
} = useAnalytics();

const activeProfileTab = ref("personal.personal-information");
const route = useRoute();
const router = useRouter();

onMounted(() => {
  plaidLink.initialize();
  openEditSectionFromQuery();
  restoreBankBanner();
});

// Deep link support: /profile?editSection=id-information opens that personal
// section's edit modal directly, so an action item can drop the investor on the
// field they need instead of the profile landing page. Waits for schema/contact
// to load, since editInformation() needs both.
function openEditSectionFromQuery() {
  const requested = route.query?.editSection;
  if (!requested) return;

  const label = owner_detail_groups.find((group) => slugify(group) === requested);
  if (!label) return;

  activeProfileTab.value = `personal.${slugify(label)}`;

  const isReady = () => !!dataStore.contact?.record_id && !!schemaStore.all_contact_schema?.length;

  // Already loaded (soft nav) — open now. No `immediate` watcher, since its
  // callback would run synchronously inside watch() and hit `stop` in its TDZ.
  if (isReady()) {
    editInformation("owner_contact", label);
    return;
  }

  // Cold load — wait for contact + schema, then open once.
  const stop = watch(isReady, (ready) => {
    if (!ready) return;
    stop();
    editInformation("owner_contact", label);
  });
}

onUnmounted(() => {
  router.replace({
    path: route.path,
    query: {},
  });
});

function handleTabAction(payload) {
  if (!payload?.action) return;
  if (payload.action === "add_joint_contact") {
    addNewJointOrEntity("joint_contact");
  }
  if (payload.action === "add_entity") {
    addNewJointOrEntity("entity_account");
  }
}

const plaidLink = usePlaidLink();

const schemaStore = useSchemaStore();
const dataStore = useDataStore();
const crudStore = useCrudStore();
const UIStore = useUIStore();

const tabs = computed(() => getTabs());

const existing_record_modal_config = ref({ open: false });
const new_joint_or_entity_modal_config = ref({ open: false });
const edit_trusted_contact_modal_config = ref({ open: false });
const delete_confirmation_modal_config = ref({ open: false });
const questionnaire_modal_config = ref({ open: false });
const new_ben_owner_modal_config = ref({ open: false });
const edit_ben_owner_modal_config = ref({ open: false });
const delete_ben_owner_modal_config = ref({ open: false });
const add_ben_owner_to_entity_modal_config = ref({ open: false });
const apply_bank_modal = ref({ open: false, bank_account_id: "" });

const opted_out_trusted_contact_form = ref(dataStore.contact?.opted_out_trusted_contact_form);

const trusted_contact_opt_out_modal_config_open = ref(false);

// --- Detect new bank accounts and persist across refresh ---
const newBankAccountId = ref(null);
const knownBankAccountIds = ref(new Set());

/**
 * On mount, restore the banner from sessionStorage.
 * Only show if the stored bank account still exists in the data
 * (covers edge cases like account deletion between sessions).
 */
function restoreBankBanner() {
  try {
    const stored = sessionStorage.getItem(BANNER_STORAGE_KEY);
    if (stored) {
      const banks = dataStore.bank_accounts || [];
      const stillExists = banks.some((b) => b.record_id === stored);
      if (stillExists) {
        newBankAccountId.value = stored;
      } else {
        sessionStorage.removeItem(BANNER_STORAGE_KEY);
      }
    }
  } catch {
    // sessionStorage unavailable (e.g. private browsing) — degrade silently
  }

  // Seed known IDs from current data so the watcher only fires on truly new additions
  knownBankAccountIds.value = new Set((dataStore.bank_accounts || []).map((b) => b.record_id));
}

/**
 * If investments haven't loaded yet when restoreBankBanner runs,
 * wait for them and re-check. This handles cold-load where
 * bank_accounts arrives after the component mounts.
 */
watch(
  () => dataStore.bank_accounts,
  (banks) => {
    if (!banks?.length) return;

    // Re-seed known IDs on every change
    const currentIds = new Set(banks.map((b) => b.record_id));

    // Detect a brand-new account (added via Plaid in this session)
    const newBank = banks.find((b) => !knownBankAccountIds.value.has(b.record_id));

    knownBankAccountIds.value = currentIds;

    if (newBank) {
      newBankAccountId.value = newBank.record_id;
      try {
        sessionStorage.setItem(BANNER_STORAGE_KEY, newBank.record_id);
      } catch {
        // silent
      }
    }

    // If we have a stored banner ID but it was removed from the data, clear it
    if (newBankAccountId.value && !currentIds.has(newBankAccountId.value)) {
      newBankAccountId.value = null;
      try {
        sessionStorage.removeItem(BANNER_STORAGE_KEY);
      } catch {
        // silent
      }
    }
  },
  { deep: true }
);

/**
 * Dismiss the banner manually without applying.
 */
function dismissBankBanner() {
  newBankAccountId.value = null;
  try {
    sessionStorage.removeItem(BANNER_STORAGE_KEY);
  } catch {
    // silent
  }
}

const showBottomDeleteButton = computed(() => {
  if (activeProfileTab.value === "entities.beneficial-owners") {
    return false;
  }

  if (activeProfileTab.value.includes("joint-contacts.")) {
    let joint_id = activeProfileTab.value.split(".")[1];
    let investments = active_investments(joint_id, "joint_contact");
    if (investments.length == 0) {
      return true;
    }
  }

  if (activeProfileTab.value.includes("entities.")) {
    let entity_id = activeProfileTab.value.split(".")[1];
    let investments = active_investments(entity_id, "entity_account");
    if (investments.length == 0) {
      return true;
    }
  }

  return false;
});
const showBottomAddButton = computed(() => {
  if (activeProfileTab.value === "entities.beneficial-owners") {
    return false;
  }

  if (activeProfileTab.value.includes("joint-contacts.")) {
    return true;
  }

  if (activeProfileTab.value.includes("entities.")) {
    return true;
  }

  return false;
});
const jointContactSubtabs = computed(() => {
  const jointTab = tabs.value?.find((t) => t.label === "Joint Contacts");
  return jointTab?.subtabs ?? [];
});

const entitySubtabs = computed(() => {
  let entityTab = tabs.value?.find((t) => t.label === "Entities");
  let subtabs = entityTab?.subtabs ?? [];
  subtabs = subtabs.filter((subtab) => subtab.key !== "entities.beneficial-owners");
  return subtabs;
});

const personalSubtabs = computed(() => {
  const personalTab = tabs.value?.find((t) => t.label === "Personal");
  return personalTab?.subtabs ?? [];
});
const beneficialOwners = computed(() => {
  if (!dataStore.beneficial_owner_contacts) return [];

  let ben_owners = dataStore.beneficial_owner_contacts?.filter(
    (contact) => contact.type === "Beneficial Owner"
  );

  for (let ben_owner of ben_owners) {
    ben_owner.name = ben_owner.first_name + " " + ben_owner.last_name;
  }

  return ben_owners;
});
function entity_beneficialOwners(entity_id) {
  if (!entity_id) return [];
  let ben_owners = dataStore.beneficial_owner_contacts?.filter((contact) =>
    contact.entity_ids?.includes(entity_id)
  );

  if (!ben_owners) ben_owners = [];

  for (let ben_owner of ben_owners) {
    ben_owner.name = ben_owner.first_name + " " + ben_owner.last_name;
  }

  if (dataStore.contact.entity_ids?.includes(entity_id)) {
    let primary_contact = {
      ...dataStore.contact,
      name: "(You) " + dataStore.contact.first_name + " " + dataStore.contact.last_name,
      is_primary_contact: true,
    };

    ben_owners.unshift(primary_contact);
  }

  return ben_owners;
}

const bankAccounts = computed(() => {
  let banks = dataStore.bank_accounts || [];
  return banks;
});

const daysUntilExpire = computed(() => {
  let id_expiration_date = dataStore.contact?.id_info?.id_expiration_date;
  if (!id_expiration_date) return 1000;
  const today = new Date();
  const expirationDate = new Date(id_expiration_date);
  const timeDiff = expirationDate.getTime() - today.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
});
function daysUntilExpireJoint(joint_id) {
  let joint_contact = dataStore.joint_contacts?.find((c) => c.record_id === joint_id);
  let id_expiration_date = joint_contact?.id_info?.id_expiration_date;

  if (!id_expiration_date) return 1000;
  const today = new Date();
  const expirationDate = new Date(id_expiration_date);
  const timeDiff = expirationDate.getTime() - today.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
}

const SECTION_GROUP_MAP = {
  "#personal.personal-information": "Personal Information",
  "#personal.residential-address": "Residential Address",
  "#personal.mailing-address": "Mailing Address",
  "#personal.id-information": "ID Information",
  "#joint-contacts.personal-information": "Personal Information",
  "#joint-contacts.residential-address": "Residential Address",
  "#joint-contacts.id-information": "ID Information",
  "#entities.entity-information": "Entity Information",
  "#entities.entity-address": "Entity Address",
};

const joint_contact_detail_groups = [
  "Personal Information",
  "Residential Address",
  "ID Information",
];
const account_detail_groups = ["Entity Information", "Entity Address"];
const owner_detail_groups = [
  "Personal Information",
  "Residential Address",
  "Mailing Address",
  "ID Information",
];

const getSchema = (section_path) => {
  const sortByDefaultGroupOrder = (a, b) =>
    get(a, "record_detail_config.default.record_detail_group_order", 0) -
    get(b, "record_detail_config.default.record_detail_group_order", 0);

  const sortByEntityGroupOrder = (a, b) =>
    get(a, "record_detail_config.entity.record_detail_group_order", 0) -
    get(b, "record_detail_config.entity.record_detail_group_order", 0);

  const sortByJointGroupOrder = (a, b) =>
    get(a, "record_detail_config.joint.record_detail_group_order", 0) -
    get(b, "record_detail_config.joint.record_detail_group_order", 0);

  if (section_path.includes("trusted_contact")) {
    return schemaStore.all_trusted_contact_schema
      .filter((item) => item.record_detail_config?.default?.record_detail_group === "Details")
      .sort(sortByDefaultGroupOrder);
  }
  if (section_path == "investor_questionnaire_income") {
    let schema = schemaStore.all_contact_schema
      .filter(
        (item) => item.record_detail_config?.default?.record_detail_group === "Income Information"
      )
      .sort(sortByDefaultGroupOrder);
    for (let item of schema) {
      item.desktop_col_span = 6;
    }
    return schema;
  }
  if (section_path == "investor_questionnaire") {
    let schema = schemaStore.all_contact_schema
      .filter((item) => item.record_detail_config?.default?.record_detail_group === "Questionnaire")
      .sort(sortByDefaultGroupOrder);
    for (let item of schema) {
      item.desktop_col_span = 6;
      item.place_holder = "Select response";
    }
    return schema;
  }
  const group = SECTION_GROUP_MAP[section_path];
  if (!group) return [];

  if (section_path.includes("#entities")) {
    return schemaStore.all_account_schema
      .filter((item) => item.record_detail_config?.entity?.record_detail_group === group)
      .sort(sortByEntityGroupOrder);
  }
  if (section_path.includes("#joint-contacts")) {
    return schemaStore.all_contact_schema
      .filter((item) => item.record_detail_config?.joint?.record_detail_group === group)
      .sort(sortByJointGroupOrder);
  }
  if (section_path.includes("#personal")) {
    return schemaStore.all_contact_schema
      .filter((item) => item.record_detail_config?.default?.record_detail_group === group)
      .sort(sortByDefaultGroupOrder);
  }
};

const getObject = (object_name, object_id, label) => {
  switch (object_name) {
    case "owner_contact":
      if (label && label.includes("ID Information") && dataStore.contact?.id_info) {
        return dataStore.contact?.id_info;
      }
      return dataStore.contact;

    case "joint_contact":
      let joint_contact = dataStore.joint_contacts?.find((c) => c.record_id === object_id)
        ? dataStore.joint_contacts?.find((c) => c.record_id === object_id)
        : {};
      if (label && label.includes("ID Information") && joint_contact?.id_info) {
        return joint_contact?.id_info;
      }
      return joint_contact;

    case "entity_account":
      return dataStore.entity_accounts?.find((a) => a.record_id === object_id)
        ? dataStore.entity_accounts?.find((a) => a.record_id === object_id)
        : {};

    case "trusted_contact":
      return dataStore.trusted_contacts?.find((t) => t.type === object_id)
        ? dataStore.trusted_contacts?.find((t) => t.type === object_id)
        : {};
    case "investor_questionnaire":
      return dataStore.contact?.suitability_info ? dataStore.contact?.suitability_info : {};

    default:
      return {};
  }
};

function slugify(str = "") {
  return str
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");
}

const getTabs = () => {
  const base = [
    {
      label: "Personal",
      icon: "UserRound",
      key: "personal",
      subtabs: [
        {
          label: "Personal Information",
          icon: "SquareUser",
          key: "personal.personal-information",
          slot_name: "personal.personal-information",
        },
        {
          label: "Residential Address",
          icon: "House",
          key: "personal.residential-address",
          slot_name: "personal.residential-address",
        },
        {
          label: "Mailing Address",
          icon: "MapPin",
          key: "personal.mailing-address",
          slot_name: "personal.mailing-address",
        },
        {
          label: "ID Information",
          icon: "FingerprintPattern",
          key: "personal.id-information",
          slot_name: "personal.id-information",
        },
      ],
    },
    { label: "Joint Contacts", icon: "Users", key: "joint-contacts", subtabs: [] },
    { label: "Entities", icon: "BriefcaseBusiness", key: "entities", subtabs: [] },
    { label: "Banking", icon: "Landmark", key: "banking" },
    { label: "Trusted Contacts", icon: "Users", key: "trusted-contacts" },
    { label: "Investor Questionnaire", icon: "CircleQuestionMark", key: "investor-questionnaire" },
  ];

  if (dataStore.joint_contacts?.length) {
    base[1].subtabs = dataStore.joint_contacts
      .map((contact) => {
        const label =
          `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim() || "Joint Contact";
        const id = contact.record_id;
        return {
          label,
          icon: "UserRound",
          record_id: id,
          key: `joint-contacts.${id}`,
          slot_name: `joint-contacts.record-${id}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  base[1].subtabs.push({
    label: "Add Joint Contact",
    icon: "PlusCircle",
    key: "joint-contacts.add",
    slot_name: "joint-contacts.add",
    action: "add_joint_contact",
  });

  if (dataStore.entity_accounts?.length) {
    base[2].subtabs = dataStore.entity_accounts
      .map((entity) => {
        const label = entity.name || "Entity";
        const id = entity.record_id;
        return {
          label,
          icon: "BriefcaseBusiness",
          record_id: id,
          key: `entities.${id}`,
          slot_name: `entities.record-${id}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  base[2].subtabs.push({
    label: "All Beneficial Owners",
    icon: "Users",
    key: "entities.beneficial-owners",
    slot_name: "entities.beneficial-owners",
  });
  base[2].subtabs.push({
    label: "Add Entity",
    icon: "PlusCircle",
    key: "entities.add",
    slot_name: "entities.add",
    action: "add_entity",
  });

  return base;
};

const active_investments = (record_id, type) => {
  if (!dataStore.investments) return [];
  if (type === "joint_contact") {
    return dataStore.investments?.filter(
      (inv) => inv.type == "Joint" && inv.joint_contact_id == record_id && inv.signature_date
    );
  } else if (type === "entity_account") {
    return dataStore.investments?.filter(
      (inv) => inv.type == "Entity" && inv.entity_account_id == record_id && inv.signature_date
    );
  }
  return [];
};

function editInformation(type, subtab, record_id) {
  let schema = [];
  let object_to_edit = {};
  let config = "default";
  let groups = [];
  let record_name = "";
  let id_info = {};
  let original_id_info = {};

  if (type === "owner_contact") {
    config = "default";
    groups = owner_detail_groups.filter((group) => group === subtab);
    schema = schemaStore.all_contact_schema.filter((item) => {
      const group = item.record_detail_config?.default?.record_detail_group || "";
      return groups.includes(group);
    });
    record_name = `${dataStore.contact.first_name} ${dataStore.contact.last_name}`;
    record_id = dataStore.contact.record_id;
    object_to_edit = JSON.parse(JSON.stringify(dataStore.contact));
    id_info = object_to_edit.id_info;
    original_id_info = JSON.parse(JSON.stringify(id_info));
    Object.keys(object_to_edit).forEach((key) => {
      if (!schema?.find((s) => s.field_name === key)) delete object_to_edit[key];
    });
  } else if (type === "joint_contact") {
    config = "joint";
    groups = joint_contact_detail_groups;
    schema = schemaStore.all_contact_schema.filter((item) => {
      const group = item.record_detail_config?.joint?.record_detail_group || "";
      return joint_contact_detail_groups.includes(group);
    });
    object_to_edit = dataStore.joint_contacts?.find((c) => c.record_id === record_id)
      ? JSON.parse(JSON.stringify(dataStore.joint_contacts?.find((c) => c.record_id === record_id)))
      : {};
    id_info = object_to_edit.id_info;
    original_id_info = JSON.parse(JSON.stringify(id_info));
    record_name = `${object_to_edit.first_name} ${object_to_edit.last_name}`;
    Object.keys(object_to_edit).forEach((key) => {
      if (!schema?.find((s) => s.field_name === key)) delete object_to_edit[key];
    });
  } else if (type === "entity_account") {
    config = "entity";
    groups = account_detail_groups;
    schema = schemaStore.all_account_schema.filter((item) => {
      const group = item.record_detail_config?.entity?.record_detail_group || "";
      return account_detail_groups.includes(group);
    });
    object_to_edit = dataStore.entity_accounts?.find((a) => a.record_id === record_id)
      ? JSON.parse(
          JSON.stringify(dataStore.entity_accounts?.find((a) => a.record_id === record_id))
        )
      : {};
    record_name = `${object_to_edit.name}`;
    Object.keys(object_to_edit).forEach((key) => {
      if (!schema?.find((s) => s.field_name === key)) delete object_to_edit[key];
    });
  }

  let flattened_schema = schemaStore.flattenSchema(schema, config);

  if (type === "owner_contact") {
    let email_field = flattened_schema.find((field) => field.field_name === "email");
    if (email_field) {
      email_field.read_only = true;
      email_field.additional_info =
        "Email cannot be edited after account creation. To change your email, please contact support.";
    }
  }

  let has_active_investments = false;
  if (type === "joint_contact") {
    has_active_investments = active_investments(record_id, "joint_contact")?.length > 0;
  } else if (type === "entity_account") {
    has_active_investments = active_investments(record_id, "entity_account")?.length > 0;
  } else if (type === "owner_contact") {
    has_active_investments = dataStore.investments?.some((inv) => inv.signature_date);
  }

  if (has_active_investments) {
    for (let schema_field of flattened_schema) {
      if (schemaStore.read_only_after_investments.includes(schema_field.field_name)) {
        schema_field.read_only = true;
      }
    }
  }

  let tabs = groups.map((group) => ({ label: group, slot_name: slugify(group), icon: "Pencil" }));
  let original_object = JSON.parse(JSON.stringify(object_to_edit));

  profileEditOpened(subtab ?? type);
  existing_record_modal_config.value = {
    type,
    subtab,
    groups,
    object_to_edit,
    original_object,
    schema: flattened_schema,
    record_id,
    original_id_info,
    tabs,
    id_info,
    record_name,
    open: true,
  };
}

async function handleDataRefresh({ type, record_id }) {
  if (type === "investor_questionnaire") {
    questionnaireSubmitted();
  } else {
    profileEditSaved(type);
  }

  let tables_to_refresh = [];
  if (type.includes("trusted")) {
    tables_to_refresh.push("trusted_contacts");
  } else if (type === "owner_contact") {
    tables_to_refresh.push("contacts");
    tables_to_refresh.push("accounts");
  } else if (type === "joint_contact") {
    tables_to_refresh.push("contacts");
  } else if (type === "entity_account") {
    tables_to_refresh.push("accounts");
  } else if (type === "investor_questionnaire") {
    tables_to_refresh.push("contacts");
  } else if (type === "beneficial_owner") {
    tables_to_refresh.push("contacts");
  }

  tables_to_refresh = [...new Set(tables_to_refresh)];

  setTimeout(async () => {
    await dataStore.setData(tables_to_refresh);
    if (type === "owner_contact") {
      syncContactPersonProperties(dataStore.contact);
    }
  }, 1000);
}

async function handleNewRecordCreated({ type, record_id }) {
  if (!type || !record_id) return;
  await dataStore.setData(["contacts", "accounts"]);
  if (type === "joint_contact") {
    jointContactAdded("joint");
    activeProfileTab.value = `joint-contacts.${record_id}`;
  } else if (type === "entity_account") {
    const entity = dataStore.entity_accounts?.find((a) => a.record_id === record_id);
    entityAdded(entity?.entity_type ?? null);
    activeProfileTab.value = `entities.${record_id}`;
  }
}

async function handleRecordDeleted({ type, record_id }) {
  await dataStore.setData(["contacts", "accounts"]);
  activeProfileTab.value = "personal.personal-information";
}

async function handleBankAccountApplied() {
  newBankAccountId.value = null;
  try {
    sessionStorage.removeItem(BANNER_STORAGE_KEY);
  } catch {
    // silent
  }
  await dataStore.setData(["investments"]);
}

function addNewJointOrEntity(type) {
  let schema;
  let object_to_edit = {};
  let id_info = {};
  let config;
  let groups;

  if (type === "joint_contact") {
    config = "joint";
    groups = joint_contact_detail_groups;
    object_to_edit.type = "Joint";
    schema = schemaStore.all_contact_schema.filter((item) => {
      const group = item.record_detail_config?.joint?.record_detail_group || "";
      return joint_contact_detail_groups.includes(group);
    });
  } else if (type === "entity_account") {
    config = "entity";
    groups = account_detail_groups;
    object_to_edit.type = "Entity";
    schema = schemaStore.all_account_schema.filter((item) => {
      const group = item.record_detail_config?.entity?.record_detail_group || "";
      return account_detail_groups.includes(group);
    });
  }

  let flattened_schema = schemaStore.flattenSchema(schema, config);
  for (let field of flattened_schema) {
    if (field.default_value) object_to_edit[field.field_name] = field.default_value;
  }

  let tabs = groups.map((group) => ({ label: group, slot_name: slugify(group), icon: "Pencil" }));

  new_joint_or_entity_modal_config.value = {
    type,
    subtab: "",
    groups,
    object_to_edit,
    id_info,
    schema: flattened_schema,
    tabs,
    open: true,
  };
}

watch(
  [opted_out_trusted_contact_form, () => trusted_contact_opt_out_modal_config_open.value],
  async ([optedOut, isModalOpen], [prevOptedOut, prevIsModalOpen]) => {
    if (optedOut && !prevOptedOut) {
      trusted_contact_opt_out_modal_config_open.value = true;
      return;
    }
    if (!optedOut && prevOptedOut) {
      dataStore.contact.opted_out_trusted_contact_form = false;
      const query = [
        { field_name: "record_id", operator: "eq", value: dataStore.contact.record_id },
      ];
      let update = { opted_out_trusted_contact_form: false };
      update = schemaStore.addCreatedUpdatedFields(update, false, true);
      try {
        await crudStore.update("contacts", query, update);
      } catch (err) {
        opted_out_trusted_contact_form.value =
          dataStore.contact?.opted_out_trusted_contact_form ?? false;
      }
      return;
    }
    if (!isModalOpen && prevIsModalOpen) {
      opted_out_trusted_contact_form.value =
        dataStore.contact?.opted_out_trusted_contact_form ?? false;
    }
  }
);

function editTrustedContactInformation(type) {
  let schema = schemaStore.all_trusted_contact_schema.filter((item) => {
    const group = item.record_detail_config?.default?.record_detail_group || "";
    return group === "Details";
  });
  let flattened_schema = schemaStore.flattenSchema(schema, "default");
  let object_to_edit = dataStore.trusted_contacts?.find((t) => t.type === type)
    ? JSON.parse(JSON.stringify(dataStore.trusted_contacts?.find((t) => t.type === type)))
    : { type, is_add: true };
  let original_object = JSON.parse(JSON.stringify(object_to_edit));
  profileEditOpened("trusted_contact");
  edit_trusted_contact_modal_config.value = {
    type,
    object_to_edit,
    schema: flattened_schema,
    original_object,
    open: true,
  };
}

function deleteJointOrEntity(type, record_id) {
  let record_name = "";
  if (type === "joint_contact") {
    let contact = dataStore.joint_contacts?.find((c) => c.record_id === record_id);
    if (contact) record_name = `${contact.first_name} ${contact.last_name}`;
  } else if (type === "entity_account") {
    let entity = dataStore.entity_accounts?.find((a) => a.record_id === record_id);
    if (entity) record_name = entity.name;
  }
  delete_confirmation_modal_config.value = { type, record_id, record_name, open: true };
}

function editQuestionnaire(subtab) {
  let schema = schemaStore.all_contact_schema.filter((item) => {
    const group = item.record_detail_config?.default?.record_detail_group || "";
    return group === "Questionnaire" || group === "Income Information";
  });
  let flattened_schema = schemaStore.flattenSchema(schema, "default");
  let object_to_edit = dataStore.contact?.suitability_info
    ? JSON.parse(JSON.stringify(dataStore.contact?.suitability_info))
    : {};
  let tabs = [
    { label: "Income Information", slot_name: "income-information", icon: "Pencil" },
    { label: "Questionnaire", slot_name: "questionnaire", icon: "Pencil" },
  ];
  profileEditOpened("questionnaire");
  questionnaire_modal_config.value = {
    type: "investor_questionnaire",
    tabs,
    groups: ["Income Information", "Questionnaire"],
    object_to_edit,
    schema: flattened_schema,
    original_object: JSON.parse(JSON.stringify(object_to_edit)),
    open: true,
    subtab,
  };
}

function addBeneficialOwner() {
  let groups = ["Personal Information", "Residential Address", "ID Information"];
  let subtab = "personal-information";
  let tabs = groups.map((group) => ({ label: group, slot_name: slugify(group), icon: "Pencil" }));
  let flattened_schema = schemaStore.flattenSchema(
    schemaStore.all_contact_schema,
    "beneficial_owner"
  );
  let filtered_schema = flattened_schema.filter((item) =>
    groups.includes(item.record_detail_group)
  );
  new_ben_owner_modal_config.value = {
    type: "beneficial_owner",
    object_to_edit: { type: "Beneficial Owner", id_info: {}, country: "United States" },
    schema: filtered_schema,
    tabs,
    groups,
    subtab,
    open: true,
  };
}

function editBeneficialOwner(ben_owner) {
  let record_id = ben_owner.record_id;
  let groups = ["Personal Information", "Residential Address", "ID Information"];
  let subtab = "personal-information";
  let tabs = groups.map((group) => ({ label: group, slot_name: slugify(group), icon: "Pencil" }));
  let flattened_schema = schemaStore.flattenSchema(
    schemaStore.all_contact_schema,
    "beneficial_owner"
  );
  let filtered_schema = flattened_schema.filter((item) =>
    groups.includes(item.record_detail_group)
  );
  let allow_delete = allowBenOwnerDelete(ben_owner);
  if (!allow_delete || ben_owner.type === "Joint") {
    for (let schema_field of filtered_schema) {
      if (schemaStore.read_only_after_investments.includes(schema_field.field_name))
        schema_field.read_only = true;
    }
  }
  let object_to_edit = dataStore.beneficial_owner_contacts?.find((c) => c.record_id === record_id)
    ? JSON.parse(
        JSON.stringify(dataStore.beneficial_owner_contacts?.find((c) => c.record_id === record_id))
      )
    : {};
  Object.keys(object_to_edit).forEach((key) => {
    if (!filtered_schema?.find((s) => s.field_name === key) && key !== "id_info" && key !== "type")
      delete object_to_edit[key];
  });
  let original_object = JSON.parse(JSON.stringify(object_to_edit));
  edit_ben_owner_modal_config.value = {
    type: "beneficial_owner",
    object_to_edit,
    original_object,
    schema: filtered_schema,
    tabs,
    groups,
    subtab,
    record_id,
    open: true,
  };
}

function allowBenOwnerDelete(ben_owner) {
  if (!ben_owner.entity_ids || ben_owner.entity_ids.length === 0) return true;
  for (let entity_id of ben_owner.entity_ids) {
    let investments = active_investments(entity_id, "entity_account");
    if (investments.length > 0) return false;
  }
  return true;
}

function deleteBeneficialOwner(ben_owner, permanent_delete, entity_id_to_remove) {
  let action = permanent_delete ? "permanently_delete" : "remove_from_entity";
  delete_ben_owner_modal_config.value = {
    type: "beneficial_owner",
    record_id: ben_owner.record_id,
    record_name: `${ben_owner.first_name} ${ben_owner.last_name}`,
    entity_ids: ben_owner.entity_ids,
    entity_id_to_remove,
    open: true,
    action,
  };
}

function handleConnectBankAccount() {
  connectBankAccountClicked();
  plaidLink.open("");
}

function addBenOwnerToEntity(entity_id) {
  let groups = ["Personal Information", "Residential Address", "ID Information"];
  let schema = schemaStore.all_contact_schema.filter((item) => {
    const group = item.record_detail_config?.beneficial_owner?.record_detail_group || "";
    return groups.includes(group);
  });
  let flattened_schema = schemaStore.flattenSchema(schema, "beneficial_owner");
  let tabs = groups.map((group) => ({ label: group, slot_name: slugify(group), icon: "Pencil" }));
  add_ben_owner_to_entity_modal_config.value = {
    entity_id,
    open: true,
    schema: flattened_schema,
    tabs,
    groups,
    object_to_edit: { type: "Beneficial Owner", id_info: {}, country: "United States" },
  };
}
</script>
